import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { useLogout } from '../hooks/useLogout.js';
import { SettingsIcon, LogOutIcon, ChevronDownIcon } from '../icons.jsx';

function Avatar({ user, className = 'h-8 w-8' }) {
  if (user.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name}
        className={`${className} rounded-full object-cover`}
      />
    );
  }
  const initials = (user.name || user.email || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div
      className={`${className} flex items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white`}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

/**
 * User avatar + dropdown in top bar.
 * Menu items: "My Account" (→ /settings), "Sign out".
 */
export function UserMenu() {
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();
  const logout = useLogout();

  if (!user) return null;

  function handleLogout() {
    logout.mutate(undefined, {
      onSuccess: () => {
        navigate('/login', { replace: true });
      },
      onError: (err) => {
        toast.error(err.message || 'Logout failed');
      },
    });
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-ink-900 transition-colors hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-1"
          aria-label="User menu"
        >
          <Avatar user={user} />
          <span className="hidden lg:inline">{user.name}</span>
          <ChevronDownIcon className="h-4 w-4 text-ink-500" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={8}
          align="end"
          className="z-50 w-64 overflow-hidden rounded-md border border-sky-300 bg-white shadow-md focus:outline-none"
        >
          <div className="border-b border-sky-300 p-4">
            <div className="flex items-center gap-3">
              <Avatar user={user} className="h-10 w-10" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-ink-900">{user.name}</div>
                <div className="truncate text-xs text-ink-500">{user.email}</div>
                {user.job_title && (
                  <div className="truncate text-xs text-ink-500">{user.job_title}</div>
                )}
              </div>
            </div>
          </div>

          <div className="p-1">
            <DropdownMenu.Item
              onSelect={() => navigate('/settings')}
              className="flex cursor-pointer items-center gap-3 rounded px-3 py-2 text-sm text-ink-900 outline-none data-[highlighted]:bg-sky-100"
            >
              <SettingsIcon className="h-4 w-4 text-ink-500" />
              My Account
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="my-1 h-px bg-sky-300" />

            <DropdownMenu.Item
              onSelect={handleLogout}
              disabled={logout.isPending}
              className="flex cursor-pointer items-center gap-3 rounded px-3 py-2 text-sm text-ink-900 outline-none data-[highlighted]:bg-sky-100 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
            >
              <LogOutIcon className="h-4 w-4 text-ink-500" />
              {logout.isPending ? 'Signing out…' : 'Sign out'}
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
